import { useQueryManager } from "@/composables/useQueryManager";
import {
  compareOldestFirst,
  isItemAvailable,
  isPartialCount,
} from "@/dashboard/inventory_management/common/portfolioItemsUtils";
import {
  buildLadders,
  inLadderOrder,
  type Ladders,
  NO_LADDERS,
  normalizeLadders,
  olderTakenInFull,
  type SelectedCounts,
  type SelectionLadder,
  settleLadders,
} from "@/dashboard/inventory_management/common/selectionLadder";
import { usePortfolioItemsStore } from "@/dashboard/inventory_management/common/usePortfolioItemsStore";
import type { FlattenedPortfolioItem, PortfolioItemTransfer } from "@/types";
import {
  computed,
  reactive,
  readonly,
  ref,
  shallowRef,
  watch,
  type Ref,
} from "vue";
import { useRoute } from "vue-router";

export type SelectionPolicy = (item: FlattenedPortfolioItem) => boolean;

/** Why a selection cannot take a row it may take nothing of, as an i18n key. */
const NOTHING_SELECTABLE_REASON =
  "dashboard.portfolio.disabled_reason_nothing_selectable";

export interface UsePortfolioSelectionOptions<K extends string = string> {
  /**
   * Predicate that decides which items are eligible for selection; the table explains a row it
   * rejects by the row's status. Defaults to {@link isItemAvailable} (status === "AVAILABLE").
   * Admin flows pass `() => true` to allow PENDING / RESERVED / etc. items.
   */
  policy?: SelectionPolicy;
  /**
   * Why a row the policy allows can never be selected here, as an i18n key; null when it can. For
   * a page's own rules, which the row's status does not explain.
   */
  disabledReason?: (item: FlattenedPortfolioItem) => string | null;
  /**
   * When true, mirror selection into `?portItemIds=` and restore it from
   * the URL on mount. Intended for deep-linkable page-level selection.
   * Modals should leave this off so their ephemeral state does not leak
   * into the route or stomp the underlying page's selection.
   */
  syncToUrl?: boolean;
  /**
   * The rows to select from. Defaults to the shared {@link usePortfolioItemsStore} cache (the
   * signed-in company's own portfolio); a page listing other rows (e.g. Soldera's inventory) passes
   * its own so they never mix with the cache.
   */
  items?: Readonly<Ref<readonly FlattenedPortfolioItem[]>>;
  /**
   * Opt-in oldest-first groups. Within a group the selection is always a prefix of its selectable
   * rows: selecting a row takes every older one in full, only the newest selected row may be
   * partial, and reducing or deselecting a row drops the newer ones. Rows outside any group behave
   * as without a ladder.
   */
  ladder?: SelectionLadder;
  /**
   * The most of a row the selection may take, capped at the row's count; a row it gives nothing is not
   * selectable. Send caps a Spanish holding at what no issuance-plan delivery is promised; unset
   * allows the whole row, since moving, selling or allocating a row is not held back by what the
   * registry can send from it.
   */
  maxCount?: (item: FlattenedPortfolioItem) => number;
  /**
   * Rows of different keys never share a selection: the first row selected fixes the key, and rows of
   * another key are not selectable until the selection is cleared. Unset allows any mix.
   *
   * @property key - The row's key
   * @property conflictReason - Why `item`, of another key, cannot join a selection holding `selected`,
   *   as an i18n key
   */
  exclusive?: {
    key: (item: FlattenedPortfolioItem) => K;
    conflictReason: (
      selected: FlattenedPortfolioItem,
      item: FlattenedPortfolioItem
    ) => string;
  };
}

/**
 * `?portItemIds=` format: a bare id selects the whole row (its `count`), `id:count` part of it. Full rows
 * stay bare so the common all-full selection is as short as the old id-only format, which still parses.
 * A row capped below its count (see `maxCount`) is always written as `id:count`, so a link opened after
 * the cap changed, or on a page without it, restores the amount the user chose rather than the cap.
 */
export const encodeSelectionParam = (
  selectedCounts: ReadonlyMap<number, number>,
  itemsMap: ReadonlyMap<number, FlattenedPortfolioItem>
): string =>
  [...selectedCounts.entries()]
    .sort(([a], [b]) => a - b)
    .map(([id, count]) => {
      // An item the cache no longer holds is written whole.
      const item = itemsMap.get(id);
      return item && isPartialCount(count, item.count)
        ? `${id}:${count}`
        : String(id);
    })
    .join(",");

/** Parses `?portItemIds=` into id → requested count; `null` means the whole item. */
export const parseSelectionParam = (
  raw: string
): Map<number, number | null> => {
  const parsed = new Map<number, number | null>();
  for (const entry of raw.split(",")) {
    const [idPart, countPart] = entry.split(":");
    const id = Number(idPart);
    if (!idPart || !Number.isInteger(id)) continue;
    const count = countPart === undefined ? NaN : Number(countPart);
    parsed.set(id, Number.isInteger(count) && count > 0 ? count : null);
  }
  return parsed;
};

export interface PortfolioSelection<K extends string = string> {
  readonly selectedItemIds: ReadonlySet<number>;
  readonly selectedItems: FlattenedPortfolioItem[];
  readonly hasSelectedItems: boolean;
  readonly totalSelectedMwh: number;
  /** How many selected items hold only part of what they allow (see {@link isPartialCount}). */
  readonly partialItemCount: number;
  readonly selectedItemsForApi: PortfolioItemTransfer[];
  /**
   * The {@link UsePortfolioSelectionOptions.exclusive} key every selected row shares; null with nothing
   * selected or no key.
   */
  readonly selectionKey: K | null;
  /** The policy, narrowed to the rows {@link PortfolioSelection.disabledReason} has no reason for. */
  readonly isSelectable: SelectionPolicy;
  /**
   * Why the selection cannot take a row the policy allows, as an i18n key: the page's
   * {@link UsePortfolioSelectionOptions.disabledReason}, nothing to select, or another exclusive key.
   * Null when the row is selectable, and when the policy rejects it (the caller explains those).
   */
  disabledReason(item: FlattenedPortfolioItem): string | null;
  /** The most of a row the selection may take: the `maxCount` option, else the whole row. */
  maxSelectableCount(item: FlattenedPortfolioItem): number;
  /**
   * True once `syncToUrl` has restored a non-empty selection from `?portItemIds=` on first load —
   * i.e. the page opened with a selection carried over from another tab or a link.
   */
  readonly restoredFromUrl: boolean;
  selectItem(itemId: number): void;
  deselectItem(itemId: number): void;
  updateItemSelectedCount(itemId: number, count: number): void;
  getSelectedCount(itemId: number): number | undefined;
  clearSelection(): void;
  setSelectedItems(items: FlattenedPortfolioItem[]): void;
  /**
   * Replaces the selection with these id → count in one update. Rows the selection may never take
   * drop, and the rest follow its rules: each count clamped, only the first row's exclusive key kept
   * and every ladder trimmed to its oldest-first prefix.
   */
  replaceSelection(counts: SelectedCounts): void;
  /**
   * Replaces the selection with these rows in full, bypassing the rules on purpose: it restores what
   * an existing order already holds, which the policy or the available amount may no longer allow. A
   * later refetch still clamps the rows like any others.
   */
  forceReplaceSelection(itemIds: readonly number[]): void;
  /**
   * Replaces the selection with up to `mwh` taken from `rows`, oldest production month first and
   * smaller rows first within a month, ties in the order of `rows` (each row in full with no target);
   * a ladder's rows are taken in the ladder's order. A row whose ladder has an older row not taken in
   * full (e.g. one `rows` leaves out) is passed over and reported as `skipped`.
   */
  fillOldestFirst(
    rows: readonly FlattenedPortfolioItem[],
    mwh: number | null
  ): { selectedMwh: number; skipped: boolean };
}

/**
 * Per-instance portfolio item selection state, decoupled from the shared
 * {@link usePortfolioItemsStore} so multiple components (e.g. a page-level
 * table and a modal) can hold independent selections concurrently.
 *
 * Selection is stored as `{itemId -> selectedCount}` in a local map; cache
 * items are looked up by id but never mutated. Callers control eligibility
 * via {@link UsePortfolioSelectionOptions.policy}; no row ever holds more than
 * its {@link UsePortfolioSelectionOptions.maxCount}.
 */
export function usePortfolioSelection<K extends string = string>(
  options: UsePortfolioSelectionOptions<K> = {}
): PortfolioSelection<K> {
  const {
    policy = isItemAvailable,
    disabledReason: pageReason,
    syncToUrl = false,
    items,
    ladder,
    maxCount,
    exclusive,
  } = options;
  const cache = items ? null : usePortfolioItemsStore();
  const allItems = computed(
    (): readonly FlattenedPortfolioItem[] =>
      items?.value ?? cache?.allItems ?? []
  );
  const itemsMap = computed((): ReadonlyMap<number, FlattenedPortfolioItem> =>
    cache ? cache.itemsMap : new Map(allItems.value.map((i) => [i.id, i]))
  );

  // Per-instance selection state. Each usePortfolioSelection() call gets its
  // own map, so multiple coexisting selections (e.g. a page-level table and a
  // modal's selection) don't collide via shared cache items. Only `commit`
  // replaces it, and never mutates it in place.
  const selectedCounts = shallowRef<SelectedCounts>(new Map());

  const selectedItemIds = computed<ReadonlySet<number>>(() => {
    return new Set(selectedCounts.value.keys());
  });

  const selectedItems = computed((): FlattenedPortfolioItem[] => {
    return allItems.value.filter((item) => selectedCounts.value.has(item.id));
  });

  const hasSelectedItems = computed(
    (): boolean => selectedCounts.value.size > 0
  );

  const totalSelectedMwh = computed((): number => {
    let total = 0;
    for (const count of selectedCounts.value.values()) total += count;
    return total;
  });

  const maxSelectableCount = (item: FlattenedPortfolioItem): number =>
    Math.max(0, Math.min(item.count, maxCount?.(item) ?? item.count));

  const partialItemCount = computed(
    (): number =>
      [...selectedCounts.value].filter(([id, count]) => {
        const item = itemsMap.value.get(id);
        return (
          item !== undefined && isPartialCount(count, maxSelectableCount(item))
        );
      }).length
  );

  // Whether the selection may take the row at all, whatever else it holds.
  const isEligible = (item: FlattenedPortfolioItem): boolean =>
    policy(item) && !pageReason?.(item);

  // The first row of `counts` the rows still hold: it fixes the exclusive key every other row shares.
  const keyRowOf = (counts: SelectedCounts): FlattenedPortfolioItem | null => {
    if (!exclusive) return null;
    for (const id of counts.keys()) {
      const item = itemsMap.value.get(id);
      if (item) return item;
    }
    return null;
  };

  const keyRow = computed(() => keyRowOf(selectedCounts.value));

  const selectionKey = computed((): K | null =>
    keyRow.value && exclusive ? exclusive.key(keyRow.value) : null
  );

  // Why a selection whose key `first` fixed cannot take a row the policy allows; null when it can.
  const rejection = (
    item: FlattenedPortfolioItem,
    first: FlattenedPortfolioItem | null
  ): string | null => {
    const reason = pageReason?.(item);
    if (reason) return reason;
    if (maxSelectableCount(item) <= 0) return NOTHING_SELECTABLE_REASON;
    if (exclusive && first && exclusive.key(first) !== exclusive.key(item))
      return exclusive.conflictReason(first, item);
    return null;
  };

  // Whether a selection holding `counts` may take the row.
  const admits = (
    counts: SelectedCounts,
    item: FlattenedPortfolioItem
  ): boolean => policy(item) && rejection(item, keyRowOf(counts)) === null;

  const disabledReason = (item: FlattenedPortfolioItem): string | null =>
    policy(item) ? rejection(item, keyRow.value) : null;

  const isSelectable: SelectionPolicy = (item) =>
    policy(item) && rejection(item, keyRow.value) === null;

  const ladders = computed((): Ladders =>
    ladder
      ? buildLadders(allItems.value, ladder, isEligible, maxSelectableCount)
      : NO_LADDERS
  );

  // The one way the selection changes. Applies the rules to `next`, in order: each count clamped to
  // what its row allows (a row the rows no longer hold, with nothing to select or at ≤ 0 drops), only
  // rows of the first row's exclusive key kept, and every ladder a prefix — settled around the rows
  // a user action changed, or trimmed when counts changed underneath (a refetch, a URL).
  const commit = (next: SelectedCounts, changedIds?: Iterable<number>) => {
    const clamped = new Map<number, number>();
    for (const [id, count] of next) {
      const item = itemsMap.value.get(id);
      const max = item ? maxSelectableCount(item) : 0;
      if (count > 0 && max > 0) clamped.set(id, Math.min(count, max));
    }
    const first = keyRowOf(clamped);
    const sharingKey =
      first && exclusive
        ? new Map(
            [...clamped].filter(([id]) => {
              const item = itemsMap.value.get(id);
              return (
                item !== undefined &&
                exclusive.key(item) === exclusive.key(first)
              );
            })
          )
        : clamped;
    const settled = changedIds
      ? settleLadders(sharingKey, ladders.value, changedIds)
      : normalizeLadders(sharingKey, ladders.value);
    // An unchanged selection keeps its identity, so a refetch that changes nothing notifies no one.
    if (!sameCounts(settled, selectedCounts.value))
      selectedCounts.value = settled;
  };

  const withCount = (id: number, count: number): Map<number, number> =>
    new Map(selectedCounts.value).set(id, count);

  const selectedItemsForApi = computed((): PortfolioItemTransfer[] => {
    const result: PortfolioItemTransfer[] = [];
    for (const [id, selected_count] of selectedCounts.value.entries()) {
      const item = itemsMap.value.get(id);
      if (!item) continue;
      result.push({ id, count: item.count, selected_count });
    }
    return result;
  });

  function getSelectedCount(itemId: number): number | undefined {
    return selectedCounts.value.get(itemId);
  }

  function selectItem(itemId: number): void {
    if (selectedCounts.value.has(itemId)) return;
    const item = itemsMap.value.get(itemId);
    if (item && isSelectable(item))
      commit(withCount(itemId, maxSelectableCount(item)), [itemId]);
  }

  function deselectItem(itemId: number): void {
    if (!selectedCounts.value.has(itemId)) return;
    const next = new Map(selectedCounts.value);
    next.delete(itemId);
    commit(next, [itemId]);
  }

  // ≤ 0 deselects the row.
  function updateItemSelectedCount(itemId: number, count: number): void {
    const item = itemsMap.value.get(itemId);
    if (item && isSelectable(item)) commit(withCount(itemId, count), [itemId]);
  }

  function clearSelection(): void {
    if (selectedCounts.value.size > 0) commit(new Map());
  }

  function replaceSelection(counts: SelectedCounts): void {
    commit(
      new Map(
        [...counts].filter(([id]) => {
          const item = itemsMap.value.get(id);
          return item !== undefined && isEligible(item);
        })
      )
    );
  }

  function forceReplaceSelection(itemIds: readonly number[]): void {
    const next = new Map<number, number>();
    for (const id of itemIds) {
      const item = itemsMap.value.get(id);
      if (item) next.set(id, item.count);
    }
    if (!sameCounts(next, selectedCounts.value)) selectedCounts.value = next;
  }

  // Reconcile selection state when the underlying cache changes (e.g. after
  // a refetch following a save). Items that disappeared must be dropped, and
  // counts must be clamped to what the new item allows — otherwise the UI keeps
  // showing stale totals while the API payload silently omits the entries,
  // and edge-case payloads would fail backend validation (selected_count >
  // count).
  watch(itemsMap, () => {
    if (selectedCounts.value.size > 0) commit(selectedCounts.value);
  });

  function setSelectedItems(items: FlattenedPortfolioItem[]): void {
    const newIds = new Set(items.map((i) => i.id));
    // Rows a removal drops (a ladder's newer months) may still be in `items`: the table hands back
    // the rows it had ticked. Only rows that were not selected before count as additions; the first
    // row kept or added fixes the exclusive key.
    const previous = selectedCounts.value;
    const removed = [...previous.keys()].filter((id) => !newIds.has(id));
    const added = items.filter(
      (item) => !previous.has(item.id) && isEligible(item)
    );
    if (removed.length === 0 && added.length === 0) return;
    const next = new Map(previous);
    for (const id of removed) next.delete(id);
    for (const item of added) next.set(item.id, maxSelectableCount(item));
    commit(next, [...removed, ...added.map((item) => item.id)]);
  }

  function fillOldestFirst(
    rows: readonly FlattenedPortfolioItem[],
    mwh: number | null
  ): { selectedMwh: number; skipped: boolean } {
    // PERF: walks a draft and commits once; a commit per row would copy the selection per row.
    const draft = new Map<number, number>();
    let remaining = mwh !== null && mwh > 0 ? mwh : Infinity;
    let skipped = false;
    const ordered = inLadderOrder(
      [...rows].sort(compareOldestFirst),
      ladders.value
    );
    for (const item of ordered) {
      if (remaining <= 0) break;
      if (!admits(draft, item)) continue;
      if (!olderTakenInFull(draft, ladders.value, item.id)) {
        skipped = true;
        continue;
      }
      const count = Math.min(maxSelectableCount(item), remaining);
      draft.set(item.id, count);
      remaining -= count;
    }
    commit(draft);
    return { selectedMwh: totalSelectedMwh.value, skipped };
  }

  const restoredFromUrl = ref(false);
  if (syncToUrl) {
    const route = useRoute();
    const { updateQuery } = useQueryManager();

    // Watch the encoded string rather than the ids so a count-only change (a partial amount) reaches
    // the URL too. The table commits counts on Enter/blur, so this never fires per keystroke.
    const encodedSelection = computed(() =>
      encodeSelectionParam(selectedCounts.value, itemsMap.value)
    );

    watch(
      encodedSelection,
      (encoded) => {
        updateQuery({ portItemIds: encoded || undefined });
      },
      { flush: "post" }
    );

    // Track whether the cache has been populated at least once so the initial
    // hydration runs after items load, not on the empty cache present on mount.
    let initialHydrationDone = false;

    const syncFromUrl = () => {
      const raw = route.query.portItemIds;
      if (typeof raw === "string") {
        if (raw === encodedSelection.value) return;
        applyUrlSelection(parseSelectionParam(raw));
      } else if (raw === undefined || raw === null) {
        clearSelection();
      }
    };

    // One batched update that restores each listed row and drops selected items the URL no longer lists.
    const applyUrlSelection = (requested: Map<number, number | null>) => {
      const next = new Map<number, number>();
      for (const [id, count] of requested) {
        const item = itemsMap.value.get(id);
        if (item) next.set(id, count ?? maxSelectableCount(item));
      }
      replaceSelection(next);
    };

    watch(() => route.query.portItemIds, syncFromUrl);

    // Deep links land before cache.fetchItems resolves, so the route watcher
    // above can't restore selection on first load — itemsMap is empty when it
    // fires. Defer the initial hydration to the first non-empty cache.
    watch(
      itemsMap,
      (newMap) => {
        if (initialHydrationDone) return;
        if (newMap.size === 0) return;
        initialHydrationDone = true;
        syncFromUrl();
        restoredFromUrl.value = selectedCounts.value.size > 0;
      },
      { immediate: true }
    );
  }

  return reactive({
    selectedItemIds,
    selectedItems,
    hasSelectedItems,
    totalSelectedMwh,
    partialItemCount,
    selectedItemsForApi,
    selectionKey,
    isSelectable,
    disabledReason,
    maxSelectableCount,
    restoredFromUrl: readonly(restoredFromUrl),
    selectItem,
    deselectItem,
    updateItemSelectedCount,
    getSelectedCount,
    clearSelection,
    setSelectedItems,
    replaceSelection,
    forceReplaceSelection,
    fillOldestFirst,
  }) as PortfolioSelection<K>;
}

const sameCounts = (a: SelectedCounts, b: SelectedCounts): boolean =>
  a.size === b.size && [...a].every(([id, count]) => b.get(id) === count);
